import { Component, DestroyRef, EventEmitter, inject, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { OrderQueries } from 'src/app/models/shared/enums/get-query';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { CompanyOrderInfo } from '../../../models/types/company-order-info.type';
import { UtilityFunctions } from 'src/app/shared/utilities/functions.models';
import { MatDialog } from '@angular/material/dialog';
import { FilterListComponent } from 'src/app/shared/components/filter-list/filter-list.component';
import { PhotographerFilterService } from 'src/app/shared/services/filters/photographer-filter.service';
import { MutationQuery } from 'src/app/models/shared/enums/mutation-query.enum';
import { UtilitiesService } from 'src/app/shared/services/utilities.service';
import { ChangeOrderStatusComponent } from 'src/app/shared/components/change-order-status/change-order-status.component';
import { Photographer } from 'src/app/models/common/photographer.models';
import { Selectable } from 'src/app/shared/models/types/selectable.type';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UnifiedOrderOutletEvent } from '../unified-order-view.component';

@Component({
	selector: 'bt-unified-order-detail-info',
	standalone: false,
	templateUrl: './unified-order-detail-info.component.html',
	styleUrls: ['./unified-order-detail-info.component.scss']
})
export class UnifiedOrderDetailInfoComponent {
	@Output() outletEvent = new EventEmitter<UnifiedOrderOutletEvent>();

	readonly #destroyRef = inject(DestroyRef);
	readonly #graphQLService = inject(GraphQLService);
	readonly #appService = inject(AppService);
	readonly #route = inject(ActivatedRoute);
	readonly #utilitiesService = inject(UtilitiesService);
	readonly #photographerService = inject(PhotographerFilterService);
	readonly #dialog = inject(MatDialog);

	orderIdParam: string | number | null = null;
	/** True when route is 'new' (creating new order). */
	isNewOrder = false;
	/** True when order can be edited: new order OR existing order with statusId === 1. */
	get isEditMode(): boolean {
		return this.isNewOrder || (this.orderInfo?.statusId === 1);
	}
	orderInfo: CompanyOrderInfo | null = null;
	form: FormGroup | null = null;
	selectedPhotographer: Selectable<Photographer> = null;
	editNotesAndDueDate = false;

	constructor() {
		this.setForm();
	}

	ngOnInit(): void {
		const raw = this.#route.parent?.snapshot?.params['orderId'];
		this.orderIdParam = raw ?? null;
		this.isNewOrder = this.orderIdParam === 'new';
		if (this.isNewOrder) {
			this.getOrderStatus();
		} else if (raw && !isNaN(Number(raw))) {
			this.getCompanyOrderInfo(Number(raw));
		}
		this.#route.parent?.params?.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((params) => {
			const id = params['orderId'] ?? null;
			this.orderIdParam = id;
			this.isNewOrder = id === 'new';
			if (!this.isNewOrder && id && !isNaN(Number(id))) {
				this.getCompanyOrderInfo(Number(id));
			}
		});
	}

	openChangeStatusModal(): void {
		if (!this.orderInfo) return;
		const dialog = this.#dialog.open(ChangeOrderStatusComponent, {
			data: { actualStatusId: this.orderInfo.statusId },
			height: '20%',
			minHeight: '20%',
			width: 'auto',
			minWidth: '25%'
		});
		dialog.afterClosed().subscribe({
			next: (res) => {
				if (res != null && res !== this.orderInfo.statusId) {
					this.updateOrderStatus(this.orderInfo.orderId, res);
				}
			}
		});
	}

	openChangePhotographerModal(): void {
		const selected = this.isNewOrder && this.selectedPhotographer
			? [this.selectedPhotographer]
			: this.orderInfo
				? [{ personId: this.orderInfo.photographerId, displayName: this.orderInfo.photographerName, displayId: this.orderInfo.photographerId }]
				: [];
		const dialog = this.#dialog.open(FilterListComponent<any>, {
			data: {
				title: 'Photographer',
				selected,
				filterService: this.#photographerService,
				singleSelection: true,
				variables: { page: 0, size: 50 }
			},
			height: '70%',
			width: 'auto',
			minWidth: '25%'
		});
		dialog.afterClosed().subscribe({
			next: (res) => {
				if (res) {
					if (this.isNewOrder && this.form) {
						this.selectedPhotographer = res[0];
						this.selectedPhotographer.selected = true;
						this.selectedPhotographer.disabled = false;
						this.form.patchValue({
							photographerId: res[0].displayId,
							photographerName: res[0].displayName
						});
					} else if (this.orderInfo) {
						this.orderInfo.photographerId = res[0].personId;
						this.orderInfo.photographerName = res[0].displayName ?? this.orderInfo.photographerName;
						this.form?.patchValue({
							photographerId: res[0].personId,
							photographerName: res[0].displayName ?? this.orderInfo.photographerName
						});
						// Photographer change is not persisted here; use Order Details (UPSERT_ORDER) to save.
					}
				}
			}
		});
	}

	confirmOrderInfo(): void {
		if (!this.form?.valid) return;
		if (this.isNewOrder) {
			this.outletEvent.emit({ type: 'savedOrderInfo', payload: this.form.value });
		} else if (this.orderInfo) {
			const previousStatusId = this.orderInfo.statusId;
			this.orderInfo.statusId = this.form.get('statusId')?.value;
			this.orderInfo.statusName = this.form.get('statusName')?.value;
			this.orderInfo.dueDate = this.form.get('dueDate')?.value;
			this.orderInfo.notes = this.form.get('notes')?.value ?? '';
			this.orderInfo.photographerId = this.form.get('photographerId')?.value;
			this.orderInfo.photographerName = this.form.get('photographerName')?.value ?? '';
			if (this.form.get('statusId')?.value !== previousStatusId) {
				this.updateOrderStatus(this.orderInfo.orderId, this.form.get('statusId')?.value);
			}
		}
	}

	toggleEditNotesAndDueDate(): void {
		this.editNotesAndDueDate = !this.editNotesAndDueDate;
	}

	saveNotesAndDueDate(): void {
		if (!this.orderInfo || !this.form) return;
		const dueDate = this.form.get('dueDate')?.value;
		const notes = this.form.get('notes')?.value;
		this.orderInfo.dueDate = dueDate;
		this.orderInfo.notes = notes;
		// Notes/due date are not persisted here; use Order Details (UPSERT_ORDER) to save.
		this.editNotesAndDueDate = false;
	}

	private setForm(): void {
		this.form = new FormGroup({
			orderId: new FormControl<number>(0),
			statusId: new FormControl<number>(0),
			statusName: new FormControl<string>(''),
			completitionDate: new FormControl<string>('-'),
			creationDate: new FormControl<Date>(new Date()),
			dueDate: new FormControl<Date | string | null>(null, { validators: [Validators.required] }),
			notes: new FormControl<string>(''),
			photographerId: new FormControl<number>(0, { validators: [Validators.required] }),
			photographerName: new FormControl<string>('', { validators: [Validators.required] })
		});
	}

	private getOrderStatus(): void {
		this.#graphQLService
			.getData(OrderQueries.GET_ORDER_STATUSES, { first: 50, after: null, search: '', ids: [1] })
			.pipe(
				map((res: any) => res['data']['orderStatuses']),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe({
				next: (data) => {
					this.form?.patchValue({
						statusId: data.nodes[0].id,
						statusName: data.nodes[0].name
					});
				}
			});
	}

	private getCompanyOrderInfo(orderId: number): void {
		this.#graphQLService
			.getData(OrderQueries.GET_COMPANY_ORDER_INFO, { companyId: this.#appService.companyId, orderId })
			.pipe(
				map((res: any) => res['data']['companyOrderInfo']['nodes'][0]),
				takeUntilDestroyed(this.#destroyRef)
			)
			.subscribe({
				next: (data) => {
					this.orderInfo = UtilityFunctions.DeepClone(data);
					this.form?.patchValue({
						orderId: this.orderInfo.orderId,
						statusId: this.orderInfo.statusId,
						statusName: this.orderInfo.statusName,
						completitionDate: this.orderInfo.completitionDate ?? '-',
						creationDate: this.orderInfo.creationDate,
						dueDate: this.orderInfo.dueDate,
						notes: this.orderInfo.notes ?? '',
						photographerId: this.orderInfo.photographerId,
						photographerName: this.orderInfo.photographerName
					});

					this.outletEvent.emit({ type: 'savedOrderInfo', payload: this.orderInfo });
				}
			});
	}

	private updateOrderStatus(orderId: number, orderStatusId: number): void {
		this.#graphQLService
			.postData(MutationQuery.UPDATE_ORDER_STATUS, {
				companyId: this.#appService.companyId,
				currentUserId: this.#appService.user.userId,
				orderId,
				orderStatusId
			})
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (res) => {
					this.#utilitiesService.showSnackbar((res.data as any).updateOrderStatus.message);
					if (this.orderInfo) {
						this.orderInfo.statusId = orderStatusId;
					}
					this.getCompanyOrderInfo(orderId);
				},
				error: (err) => {
					this.#utilitiesService.showSnackbar(err);
				}
			});
	}
}
